import { Alert, Box, Button, Heading, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IAsset } from '@frontend/components/interfaces';
import { Figure } from '@frontend/components/elements/figure/Figure';
import { AssetThumbnailsWithHeader } from '@frontend/components/pages/episodes/common';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { Modal } from '../../../../elements/modals/Modal';

interface IModalUploadPictureFromDermaAppProps {
    isOpen: boolean;
    onCancel: () => Promise<void>;
    onProceed: () => Promise<void>;
    requestId: string;
}

export function ModalUploadPictureFromDermaApp(props: IModalUploadPictureFromDermaAppProps) {
    const [assets, setAssets] = useState<IAsset[]>([]);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | undefined = undefined;
        if (props.isOpen) {
            pollAssets();
            intervalId = setInterval(() => pollAssets(), 4000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [props.isOpen]);

    return (
        <Modal
            actionButtons={[
                <Button
                    key="cancel"
                    onClick={async () => {
                        await props.onCancel();
                    }}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>,
                <Button
                    isDisabled={assets.length < 1}
                    key="proceed"
                    onClick={async () => {
                        await props.onProceed();
                    }}
                >
                    {translateKey('common.proceed')}
                </Button>,
            ]}
            isOpen={props.isOpen}
            onClose={async () => {
                await props.onCancel();
            }}
            size="medium"
            title={translateKey('dermaModal.title')}
        >
            <Text>{translateKey('dermaModal.instructions')}</Text>
            {renderAlert(assets.length)}
            {renderImages(assets)}
        </Modal>
    );

    async function pollAssets() {
        const requestResult = (await fetchJson(`/api/requests/${props.requestId}?showAssets=true`, 'GET')).json;
        setAssets(requestResult.assets);
    }

    function renderImages(thumbnails: IAsset[]) {
        return thumbnails.length > 0 ? (
            <Box
                mb={4}
                mt={8}
            >
                <AssetThumbnailsWithHeader
                    assets={assets}
                    header={translateKey('dermaModal.uploadedPhotos')}
                    id={props.requestId}
                />
            </Box>
        ) : (
            <Box
                display="flex"
                justifyContent="center"
                mb={4}
                mt={8}
            >
                <Figure
                    alt="Upload image from app"
                    imageUrl={'/assets/images/derma-consult-upload-image-from-app.svg'}
                />
            </Box>
        );
    }

    function renderAlert(numberOfAssets: number) {
        return numberOfAssets > 0 ? (
            <Alert
                appearance="info"
                isLoading
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    marginBottom: 0,
                }}
            >
                <Box
                    alignItems={'center'}
                    display={'flex'}
                    flexDirection={'row'}
                >
                    <Text
                        mr={5}
                        textAlign="center"
                    >
                        {translateKey('dermaModal.connectedPhotos')}
                    </Text>
                    <Heading
                        size="xlarge"
                        textAlign="center"
                    >
                        {assets.length}
                    </Heading>
                </Box>
            </Alert>
        ) : (
            <Alert
                appearance="info"
                isLoading
                style={{ marginBottom: 0 }}
            >
                {translateKey('dermaModal.checkingForPhotos')}
            </Alert>
        );
    }
}
